import React, { useState } from 'react';
import { StarIcon, XIcon } from 'lucide-react';
import { AuthorSignature } from '../components/AuthorSignature';
type ModalType = 'terms' | 'privacy' | 'contact' | null;
export function BiosandBiblePage() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <div className="min-h-screen w-full bg-[#f8f7f4] font-sans text-[#1e293b] selection:bg-[#b91c1c] selection:text-white" data-id="element-98">
      {/* 1. Top Nav Bar */}
      <header className="bg-[#002868] text-white w-full" data-id="element-99">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-12 flex items-center justify-between" data-id="element-100">
          <div className="flex items-center gap-2 font-bold text-lg tracking-tight" data-id="element-101">
            <StarIcon className="w-5 h-5 fill-current text-[#b91c1c]" data-id="element-102" />
            <span data-id="element-103">PatriotsInformer</span>
          </div>
        </div>
      </header>

      {/* 2. Breadcrumb Bar */}
      <div className="bg-white border-b border-gray-200 w-full" data-id="element-104"></div>

      {/* Main Content Area */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-12" style={{
      paddingTop: '2px'
    }} data-id="element-105">
        {/* 3. Article Section */}
        <article className="space-y-6" data-id="element-106">
          {/* Title & Meta */}
          <div className="space-y-2" data-id="element-107">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#0f172a] tracking-tight leading-tight" data-id="element-108">
              The Biosand Bible: The $150 Filter the Missionaries Took to
              Honduras
            </h1>
          </div>

          {/* Author Bio */}
          <div className="flex flex-row items-center gap-4 sm:gap-6" data-id="element-109">
            <img src="https://cdn.magicpatterns.com/uploads/qCiNdfGtT6ARUfiGnS7VJm/jack-turner-avatar-2.jpg" alt="Jack Turner" className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-gray-200 shadow-sm flex-shrink-0" data-id="element-110" />
            <div className="text-left space-y-1" data-id="element-111">
              <h3 className="font-bold text-[#0f172a] text-lg" data-id="element-112">Jack Turner</h3>
              <p className="text-gray-500 text-sm" data-id="element-113">
                Head of research at PatriotsInformer.com
              </p>
              <p className="text-gray-400 text-xs uppercase tracking-wide pt-1" data-id="element-114">
                May 5, 2026 · 4 min read
              </p>
            </div>
          </div>

          {/* Article Body */}
          <div className="space-y-6 text-lg text-[#334155] leading-relaxed" data-id="element-115">
            <p data-id="element-116">
              A Canadian engineer named David Manz took a thousand-year-old
              water filtering technique used in European cities and shrunk it
              down to something a single household could build in a weekend.
            </p>
            <p data-id="element-117">
              He called it the <b data-id="element-118">biosand filter</b>, and he gave the design
              away free.
            </p>
            <p data-id="element-119">
              Missionaries took it to Honduras, Cambodia, Nicaragua. Villages
              with cholera-grade water turned it into something kids could
              drink. No power. No replacement cartridges. No permits.
            </p>
            <p data-id="element-120">
              The whole thing runs on gravity, sand, and a thin layer of living
              bacteria on top.{' '}
              <b data-id="element-121">That layer is the part most people get wrong.</b>
            </p>

            <h2 className="text-2xl sm:text-3xl font-bold text-[#0f172a] pt-4" data-id="element-122">
              The schmutzdecke
            </h2>

            <p data-id="element-123">
              The piece that makes a biosand filter different from every YouTube
              sand-bucket build is the <b data-id="element-124">schmutzdecke</b> — German for "dirt
              blanket." It's a living, two-millimeter-thick layer of
              microorganisms that grows on the very top of the fine sand over
              the first two to three weeks of regular use. It eats the pathogens
              before the sand below filters out the rest.
            </p>
            <p data-id="element-125">
              That layer is the secret. Without it, you have a glorified sand
              bucket. With it, you have a filter that competes with commercial
              units that cost ten times as much.
            </p>
            <p data-id="element-126">To keep the schmutzdecke alive you need four things:</p>

            <ul className="space-y-4 pl-6 list-disc marker:text-[#b91c1c]" data-id="element-127">
              <li data-id="element-128">
                <b data-id="element-129">Time to mature.</b> Two to three weeks of daily use before
                the layer is fully alive.
              </li>
              <li data-id="element-130">
                <b data-id="element-131">Constant moisture.</b> A standing water level always above
                the sand. If the sand dries out, the layer dies and you start
                over.
              </li>
              <li data-id="element-132">
                <b data-id="element-133">Oxygen.</b> Which it gets from that standing water — that's
                why the diffuser plate above the sand matters.
              </li>
              <li data-id="element-134">
                <b data-id="element-135">Regular but gentle use.</b> Pouring water in too aggressively
                scours the layer. Letting the filter sit unused for weeks
                starves it.
              </li>
            </ul>

            <p data-id="element-136">
              Skip these and you have a slow, ineffective sand filter. Honor
              them and you have what the missionaries had in Honduras.
            </p>

            <blockquote className="border-l-4 border-[#b91c1c] pl-6 py-2 italic text-[#0f172a]" data-id="element-137">
              The Lord gave us sand, gravity, and a layer of bacteria that does
              most of the work. A biosand filter is gratitude in physical form.
            </blockquote>

            <p data-id="element-138">
              Build it before you need it. Build it slowly so you understand it.
              Build it with your son or your daughter so they understand it too.
            </p>
          </div>

          {/* CTA Callout Box */}
        </article>

        {/* 4. 'You Might Also Like' Section */}
        <section className="space-y-8 pt-8 border-t border-gray-200" data-id="element-139">
          <h2 className="text-3xl font-bold text-[#0f172a]" data-id="element-140">
            You Might Also Like:
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8" data-id="element-141">
            {/* Card 1 */}
            <div className="group cursor-pointer space-y-3" data-id="element-142">
              <a href="https://trk.adsdomredir.com/click/2" className="block aspect-[4/3] overflow-hidden rounded-lg shadow-md" data-id="element-143">
                <img src="https://cdn.magicpatterns.com/uploads/tfBxRA9s5qgvhpKFp12h5o/unnamed_(7).jpg" alt="The Lost SuperFoods book" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-144" />
              </a>
              <h3 className="font-bold text-lg text-[#0f172a] leading-tight group-hover:text-[#002868] transition-colors" data-id="element-145">
                <a href="https://trk.adsdomredir.com/click/2" data-id="element-146">
                  Superfoods That You Can Store Without Refrigeration for Years
                </a>
              </h3>
            </div>

            {/* Card 2 */}
            <div className="group cursor-pointer space-y-3" data-id="element-147">
              <a href="https://trk.adsdomredir.com/click/3" className="block aspect-[4/3] overflow-hidden rounded-lg shadow-md" data-id="element-148">
                <img src="https://cdn.magicpatterns.com/uploads/aeW4oPiWoeR5Wzq6FuUxBZ/new5notext.png" alt="Vegetable garden" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-149" />
              </a>
              <h3 className="font-bold text-lg text-[#0f172a] leading-tight group-hover:text-[#002868] transition-colors" data-id="element-150">
                <a href="https://trk.adsdomredir.com/click/3" data-id="element-151">
                  The Underground Energy Method They Don't Want You To Know
                  About
                </a>
              </h3>
            </div>

            {/* Card 3 */}
            <div className="group cursor-pointer space-y-3" data-id="element-152">
              <a href="https://trk.adsdomredir.com/click/4" className="block aspect-[4/3] overflow-hidden rounded-lg shadow-md" data-id="element-153">
                <img src="https://cdn.magicpatterns.com/uploads/7ksskNxA12GTBLDP2Kydn9/img18.png" alt="Man holding tools" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-154" />
              </a>
              <h3 className="font-bold text-lg text-[#0f172a] leading-tight group-hover:text-[#002868] transition-colors" data-id="element-155">
                <a href="https://trk.adsdomredir.com/click/4" data-id="element-156">
                  Nature-Inspired 3D Solar Sphere Generates Energy On Demand
                  (Watch Video)
                </a>
              </h3>
            </div>
          </div>

          <AuthorSignature data-id="element-157" />
        </section>
      </main>

      {/* 5. Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12 py-12" data-id="element-158">
        <div className="max-w-4xl mx-auto px-4 text-center space-y-6" data-id="element-159">
          <div className="flex items-center justify-center gap-2 text-[#b91c1c]" data-id="element-160">
            <div className="relative w-10 h-10" data-id="element-161">
              <StarIcon className="w-10 h-10 fill-current text-[#002868]" data-id="element-162" />
              <StarIcon className="w-4 h-4 fill-current text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-163" />
            </div>
          </div>

          <div className="text-xs text-gray-500 space-y-4 max-w-2xl mx-auto leading-relaxed" data-id="element-164">
            <p data-id="element-165">
              © 2018-2026 PatriotsInformer Media, LLC | All Rights Reserved |
              63 N. Burritt - Buffalo, WY 82834 United States
            </p>
            <p className="space-x-2" data-id="element-166">
              <button onClick={() => setActiveModal('terms')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-167">
                Terms & Conditions
              </button>
              <span data-id="element-168">|</span>
              <button onClick={() => setActiveModal('privacy')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-169">
                Privacy Policy
              </button>
              <span data-id="element-170">|</span>
              <button onClick={() => setActiveModal('contact')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-171">
                Contact Us
              </button>
            </p>
            <p data-id="element-172">
              No part of this report may be reproduced or placed on any
              electronic medium without written permission from the publisher.
              Information contained herein is obtained from sources believed to
              be reliable, but its accuracy cannot be guaranteed.
            </p>
          </div>
        </div>
      </footer>

      {/* Modals */}
      {activeModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setActiveModal(null)} data-id="element-173">
          <div className="absolute inset-0 bg-black/50" data-id="element-174" />
          <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-175">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between" data-id="element-176">
              <h2 className="text-lg font-bold text-[#0f172a]" data-id="element-177">
                {activeModal === 'terms' && 'Terms & Conditions'}
                {activeModal === 'privacy' && 'Privacy Policy'}
                {activeModal === 'contact' && 'Contact Us'}
              </h2>
              <button onClick={() => setActiveModal(null)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors" data-id="element-178">
                <XIcon className="w-5 h-5 text-gray-500" data-id="element-179" />
              </button>
            </div>

            <div className="px-6 py-6 text-sm text-[#334155] leading-relaxed space-y-4" data-id="element-180">
              {activeModal === 'terms' && <>
                  <p className="font-semibold text-[#0f172a]" data-id="element-181">
                    Last Updated: February 2026
                  </p>
                  <p data-id="element-182">
                    Welcome to PatriotsInformer.com. By accessing or using our
                    website, you agree to be bound by these Terms & Conditions.
                    Please read them carefully before using our services.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-183">
                    1. Acceptance of Terms
                  </h3>
                  <p data-id="element-184">
                    By accessing this website, you acknowledge that you have
                    read, understood, and agree to be bound by these Terms &
                    Conditions. If you do not agree, please do not use this
                    website.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-185">
                    2. Use of Content
                  </h3>
                  <p data-id="element-186">
                    All content on this website, including text, graphics,
                    images, and other material, is for informational purposes
                    only. No part of this report may be reproduced or placed on
                    any electronic medium without written permission from the
                    publisher.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-187">
                    3. Disclaimer
                  </h3>
                  <p data-id="element-188">
                    Information contained herein is obtained from sources
                    believed to be reliable, but its accuracy cannot be
                    guaranteed. The content is not intended as professional
                    advice. Always consult qualified professionals for specific
                    guidance.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-189">
                    4. Limitation of Liability
                  </h3>
                  <p data-id="element-190">
                    PatriotsInformer Media, LLC shall not be liable for any
                    damages arising from the use or inability to use the
                    materials on this website.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-191">
                    5. Governing Law
                  </h3>
                  <p data-id="element-192">
                    These terms shall be governed by and construed in accordance
                    with the laws of the State of Texas, without regard to its
                    conflict of law provisions.
                  </p>
                  <p className="pt-2 text-xs text-gray-400" data-id="element-193">
                    © 2018-2026 PatriotsInformer Media, LLC. All Rights
                    Reserved.
                  </p>
                </>}

              {activeModal === 'privacy' && <>
                  <p className="font-semibold text-[#0f172a]" data-id="element-194">
                    Last Updated: February 2026
                  </p>
                  <p data-id="element-195">
                    PatriotsInformer Media, LLC ("we," "us," or "our") is
                    committed to protecting your privacy. This Privacy Policy
                    explains how we collect, use, and safeguard your
                    information.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-196">
                    1. Information We Collect
                  </h3>
                  <p data-id="element-197">
                    We may collect personal information such as your name and
                    email address when you voluntarily submit it through our
                    opt-in forms. We also collect non-personal information such
                    as browser type, IP address, and pages visited.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-198">
                    2. How We Use Your Information
                  </h3>
                  <p data-id="element-199">
                    We use the information we collect to send you the content
                    you requested, communicate updates and offers, improve our
                    website and services, and comply with legal obligations.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-200">
                    3. Email Communications
                  </h3>
                  <p data-id="element-201">
                    By providing your email address, you consent to receive
                    emails from us. You can unsubscribe at any time by clicking
                    the "unsubscribe" link at the bottom of any email.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-202">
                    4. Third-Party Sharing
                  </h3>
                  <p data-id="element-203">
                    We do not sell, trade, or rent your personal information to
                    third parties. We may share information with trusted service
                    providers who assist us in operating our website.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-204">
                    5. Data Security
                  </h3>
                  <p data-id="element-205">
                    We implement reasonable security measures to protect your
                    personal information. However, no method of transmission
                    over the Internet is 100% secure.
                  </p>
                  <p className="pt-2 text-xs text-gray-400" data-id="element-206">
                    © 2018-2026 PatriotsInformer Media, LLC. All Rights
                    Reserved.
                  </p>
                </>}

              {activeModal === 'contact' && <>
                  <p data-id="element-207">
                    We'd love to hear from you. Feel free to reach out using the
                    information below:
                  </p>
                  <div className="bg-[#f8f7f4] rounded-lg p-5 space-y-3 border border-gray-200" data-id="element-208">
                    <h3 className="font-bold text-[#0f172a]" data-id="element-209">
                      PatriotsInformer Media, LLC
                    </h3>
                    <p data-id="element-210">
                      10160 Highway 242 Ste 800
                      <br data-id="element-211" />
                      PMB 1089
                      <br data-id="element-212" />
                      Conroe, TX 77385
                    </p>
                    <p data-id="element-213">
                      <span className="font-semibold text-[#0f172a]" data-id="element-214">
                        Email:
                      </span>{' '}
                      <a href="mailto:support@patriotsinformer.com" className="text-[#002868] underline" data-id="element-215">
                        support@patriotsinformer.com
                      </a>
                    </p>
                  </div>
                  <p className="pt-2 text-xs text-gray-400" data-id="element-216">
                    © 2018-2026 PatriotsInformer Media, LLC. All Rights
                    Reserved.
                  </p>
                </>}
            </div>
          </div>
        </div>}
    </div>;
}